/**
 * ITAM Wave 1 / F4 — asset import wizard.
 *
 * Upload → column mapping (auto-mapped by header name, manual override) →
 * dry-run diff → commit → job summary. Composed entirely from the ITAM design
 * system (SectionCard / tableSx / StatusPill / EmptyState / StatTile) plus the
 * MUI Stepper the rest of the app already uses for wizards.
 *
 * The dry run is the safety rail: nothing in the fleet changes until the
 * operator sees the diff and presses Import.
 */
import React, { useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, LinearProgress, MenuItem, Step, StepLabel, Stepper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useTheme,
} from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import apiClient from '../../services/apiClient';
import { EmptyState, SectionCard, StatTile, StatusPill, tableSx } from './itamUi';
import {
  API, ASSET_IMPORT_FIELDS, FINANCIAL_IMPORT_FIELDS, fmtNum, IMPORT_ACTION_META,
  VULN_IMPORT_FIELDS,
} from './inventoryUtils';

const STEPS = ['Upload', 'Map columns', 'Preview', 'Done'];

/**
 * Preview columns per import kind. The asset/financials shape is unchanged;
 * the advisory shape (ITAM Wave 2 / G3) reuses the same wizard, dry run and
 * error reporting rather than forking a second importer.
 */
const PREVIEW_COLUMNS = {
  assets: ['Row', 'Action', 'Asset tag', 'Hostname', 'Serial', 'Match / problem', 'Fields'],
  advisories: ['Row', 'Action', 'CVE', 'Product', 'From', 'Fixed in', 'Severity', 'Problem'],
};

/** One import row rendered as a table line. */
const RowLine = ({ r, advisory }) => {
  const meta = IMPORT_ACTION_META[r.action] || IMPORT_ACTION_META.skipped;
  if (advisory) {
    return (
      <TableRow hover>
        <TableCell sx={{ color: 'text.secondary' }}>{r.row}</TableCell>
        <TableCell><StatusPill tone={meta.tone} label={meta.label} /></TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.cveId || '—'}</TableCell>
        <TableCell>{r.product || '—'}</TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.introducedVersion || '—'}</TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.fixedVersion || '—'}</TableCell>
        <TableCell sx={{ color: 'text.secondary' }}>{r.severity || '—'}</TableCell>
        <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{r.error || '—'}</TableCell>
      </TableRow>
    );
  }
  return (
    <TableRow hover>
      <TableCell sx={{ color: 'text.secondary' }}>{r.row}</TableCell>
      <TableCell><StatusPill tone={meta.tone} label={meta.label} /></TableCell>
      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.assetTag || '—'}</TableCell>
      <TableCell>{r.hostName || '—'}</TableCell>
      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.serialNumber || '—'}</TableCell>
      <TableCell sx={{ color: 'text.secondary' }}>
        {r.error || (r.matchedBy ? `matched by ${r.matchedBy}` : (r.action === 'created' ? 'new asset' : '—'))}
      </TableCell>
      <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>
        {(r.changes || []).join(', ') || '—'}
      </TableCell>
    </TableRow>
  );
};

/**
 * @param {'assets'|'financials'|'advisories'} kind which importer to drive
 * @param {string} [endpointOverride] where to POST. The advisory importer moved
 *        to the OPERATOR surface (/api/operator/cve-feed) when advisories became
 *        one platform-wide corpus instead of a per-tenant upload; the wizard
 *        itself is unchanged, so the caller supplies the URL rather than this
 *        component growing a second copy of the whole flow.
 * @param {string} [titleOverride] dialog heading, for the same reason
 * @param {(dirty:boolean)=>void} onClose parent refetches when dirty
 */
const AssetImportDialog = ({ kind = 'assets', endpointOverride, titleOverride, onClose, notify }) => {
  const theme = useTheme();
  const fileRef = useRef(null);
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dry, setDry] = useState(null);        // dry-run result (headers, mapping, rows)
  const [result, setResult] = useState(null);  // committed result
  const [mapping, setMapping] = useState({});  // columnIndex -> canonical field
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);

  const financial = kind === 'financials';
  const advisory = kind === 'advisories';
  let fields = ASSET_IMPORT_FIELDS;
  if (financial) fields = FINANCIAL_IMPORT_FIELDS;
  if (advisory) fields = VULN_IMPORT_FIELDS;
  let endpoint = `${API}/import`;
  if (financial) endpoint = `${API}/financials/import`;
  if (advisory) endpoint = '/api/operator/cve-feed/advisories/import';
  if (endpointOverride) endpoint = endpointOverride;
  let title = 'Import assets';
  if (financial) title = 'Import financials';
  if (advisory) title = 'Import vulnerability advisories';
  if (titleOverride) title = titleOverride;

  const post = async (dryRun, mapOverride) => {
    const data = new FormData();
    data.append('file', file);
    const params = { dryRun };
    if (mapOverride && Object.keys(mapOverride).length) params.mapping = JSON.stringify(mapOverride);
    const res = await apiClient.post(endpoint, data, {
      params, headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  };

  /** Upload → parse + auto-map on the server, then show the mapping step. */
  const onUpload = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const d = await post(true, null);
      setDry(d);
      setMapping(d.mapping || {});
      setStep(1);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  /** Re-run the dry run with the operator's mapping and show the diff. */
  const onPreview = async () => {
    setBusy(true);
    setError(null);
    try {
      const d = await post(true, mapping);
      setDry(d);
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  /** Commit — the server re-validates every row from scratch. */
  const onCommit = async () => {
    setBusy(true);
    setError(null);
    try {
      const d = await post(false, mapping);
      setResult(d);
      setDirty(true);
      setStep(3);
      notify?.({ sev: 'success', msg: `Imported: ${d.rowsCreated} created, ${d.rowsUpdated} updated` });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      notify?.({ sev: 'error', msg: `Import failed: ${e.response?.data?.error || e.message}` });
    } finally {
      setBusy(false);
    }
  };

  const downloadErrors = async (jobId) => {
    try {
      const res = await apiClient.get(`${API}/import/${jobId}/errors`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `import-${jobId}-errors.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      notify?.({ sev: 'error', msg: `Download failed: ${e.response?.data?.error || e.message}` });
    }
  };

  const headers = dry?.headers || [];
  const shown = result || dry;

  /**
   * A key column must be mapped or the server will refuse the import. Assets and
   * financials need ANY one matching key; an advisory needs EVERY key (product
   * and CVE), because neither can be inferred from the other.
   */
  const keyMapped = useMemo(() => {
    const keys = fields.filter((f) => f.key).map((f) => f.value);
    const mapped = Object.values(mapping);
    return advisory
      ? keys.every((k) => mapped.includes(k))
      : mapped.some((v) => keys.includes(v));
  }, [mapping, fields, advisory]);

  const setColumn = (idx, value) => {
    setMapping((prev) => {
      const next = { ...prev };
      // A canonical field can only be claimed by one column.
      Object.keys(next).forEach((k) => { if (next[k] === value) delete next[k]; });
      if (value) next[idx] = value; else delete next[idx];
      return next;
    });
  };

  return (
    <Dialog open onClose={busy ? undefined : () => onClose(dirty)} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2, backgroundColor: 'background.default' }}>
        <Stepper activeStep={step} sx={{ mb: 1 }} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {busy && <LinearProgress />}
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        {/* ── 0 · upload ─────────────────────────────────────────────────── */}
        {step === 0 && (
          <SectionCard
            title="Choose a CSV file"
            subtitle={(() => {
              if (advisory) {
                return 'Each row is one advisory for one product and one version range. '
                     + 'Rows with no product, no ID, or no usable version range are reported as errors — never imported half-formed.';
              }
              return financial
                ? 'Rows are matched to existing assets by asset tag or serial — a financials import never creates assets.'
                : 'Assets are matched by asset tag, then serial, then hostname. Unmatched rows create new assets.';
            })()}
            icon={<UploadFileOutlinedIcon />}
          >
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileOutlinedIcon />}
              fullWidth
              sx={{ py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              {file ? file.name : 'Select CSV file'}
              <input
                type="file"
                accept=".csv,text/csv"
                hidden
                ref={fileRef}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) { setFile(f); setError(null); }
                  e.target.value = '';   // allow re-picking the same file
                }}
              />
            </Button>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.5 }}>
              CSV only (Excel files must be saved as CSV first). Columns are auto-mapped by
              header name and you can override every one on the next step.
              {advisory
                ? ' At least one of “first affected version” and “fixed in version” must be present, '
                  + 'and every version given must be dotted-numeric — an advisory that cannot be evaluated is not imported.'
                : ' Agent-reported hardware, OS and network facts are never overwritten by an import.'}
            </Typography>
          </SectionCard>
        )}

        {/* ── 1 · column mapping ─────────────────────────────────────────── */}
        {step === 1 && (
          <SectionCard
            title="Map columns"
            subtitle={`${headers.length} column${headers.length === 1 ? '' : 's'} found — auto-mapped by header name`}
            icon={<TableChartOutlinedIcon />}
            contentSx={{ p: 0 }}
          >
            <TableContainer sx={{ maxHeight: 380 }}>
              <Table stickyHeader size="small" sx={tableSx(theme)}>
                <TableHead>
                  <TableRow>
                    <TableCell>CSV column</TableCell>
                    <TableCell>Maps to</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {headers.map((h, i) => (
                    <TableRow key={`${h}-${i}`} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{h || `(column ${i + 1})`}</TableCell>
                      <TableCell sx={{ width: 260 }}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={mapping[i] || mapping[String(i)] || ''}
                          onChange={(e) => setColumn(i, e.target.value)}
                        >
                          <MenuItem value=""><em>Ignore this column</em></MenuItem>
                          {fields.map((f) => (
                            <MenuItem key={f.value} value={f.value}>
                              {f.label}{f.key ? ' · matching key' : ''}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {!keyMapped && (
              <Alert severity="warning" sx={{ m: 1.5 }}>
                {advisory
                  ? 'Map both required columns — CVE / advisory ID and Product — before continuing.'
                  : 'Map at least one matching key — asset tag, serial number or hostname — so rows '
                    + 'can be matched to existing assets.'}
              </Alert>
            )}
          </SectionCard>
        )}

        {/* ── 2/3 · diff + summary ───────────────────────────────────────── */}
        {(step === 2 || step === 3) && shown && (
          <>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
              <StatTile label="To create" value={fmtNum(shown.rowsCreated)} accent={theme.palette.success.main} />
              <StatTile label="To update" value={fmtNum(shown.rowsUpdated)} accent={theme.palette.info.main} />
              <StatTile label="No change" value={fmtNum(shown.rowsSkipped)} accent={theme.palette.text.secondary} />
              <StatTile label="Errors" value={fmtNum(shown.rowsErrored)} accent={theme.palette.error.main} />
            </Box>

            {step === 2 && (
              <Alert severity="info">
                This is a dry run — nothing has changed yet. Review the rows below, then press Import.
              </Alert>
            )}
            {step === 3 && (
              <Alert severity={shown.rowsErrored > 0 ? 'warning' : 'success'}>
                {shown.jobId ? `Import committed (job #${shown.jobId}).` : 'Import committed.'}
                {advisory
                  ? ' Findings are recalculated on the next scan — press “Rescan now” to see them immediately.'
                  : ''}
                {shown.rowsErrored > 0 && (shown.jobId
                  ? ' Some rows were skipped — download the error report below.'
                  : ' Some rows were skipped — see the Problem column below.')}
              </Alert>
            )}

            <SectionCard
              title={step === 2 ? 'Preview' : 'Result'}
              subtitle={shown.truncatedPreview
                ? `first ${(shown.rows || []).length} of ${fmtNum(shown.rowsTotal)} rows`
                : `${fmtNum(shown.rowsTotal)} row${shown.rowsTotal === 1 ? '' : 's'}`}
              icon={<FactCheckOutlinedIcon />}
              action={shown.rowsErrored > 0 && shown.jobId ? (
                <Button
                  size="small"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={() => downloadErrors(shown.jobId)}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Error report
                </Button>
              ) : undefined}
              contentSx={{ p: 0 }}
            >
              {(shown.rows || []).length === 0 ? (
                <EmptyState title="No rows" body="The file produced no importable rows." compact />
              ) : (
                <TableContainer sx={{ maxHeight: 360 }}>
                  <Table stickyHeader size="small" sx={tableSx(theme)}>
                    <TableHead>
                      <TableRow>
                        {(advisory ? PREVIEW_COLUMNS.advisories : PREVIEW_COLUMNS.assets)
                          .map((h) => <TableCell key={h}>{h}</TableCell>)}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(shown.rows || []).map((r) => (
                        <RowLine key={r.row} r={r} advisory={advisory} />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </SectionCard>

            {(shown.unmappedHeaders || []).length > 0 && step === 2 && (
              <Typography variant="caption" sx={{ color: 'text.secondary', px: 0.5 }}>
                Ignored columns: {shown.unmappedHeaders.join(', ')}
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={() => onClose(dirty)} color="inherit" disabled={busy}>
          {step === 3 ? 'Close' : 'Cancel'}
        </Button>
        <Box sx={{ flex: 1 }} />
        {step > 0 && step < 3 && (
          <Button onClick={() => setStep(step - 1)} disabled={busy}>Back</Button>
        )}
        {step === 0 && (
          <Button
            variant="contained"
            onClick={onUpload}
            disabled={!file || busy}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Continue
          </Button>
        )}
        {step === 1 && (
          <Button
            variant="contained"
            onClick={onPreview}
            disabled={busy || !keyMapped}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Dry run
          </Button>
        )}
        {step === 2 && (
          <Button
            variant="contained"
            onClick={onCommit}
            disabled={busy || (dry?.rowsCreated + dry?.rowsUpdated) === 0}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Import {fmtNum((dry?.rowsCreated || 0) + (dry?.rowsUpdated || 0))} row(s)
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AssetImportDialog;
